import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Paper,
  Alert,
  Container,
  CircularProgress,
  Snackbar,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import PendingIcon from '@mui/icons-material/Pending';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { clearCachesAfterUpload } from '../services/api';

// Chola Insurance theme colors - matching Dashboard
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD' // Darker blue for hover states
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
  border: `1px solid ${CHOLA_COLORS.grey}`,
  overflow: 'hidden',
}));

const FileTypeCard = styled(Card)<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(2),
  cursor: 'pointer',
  height: '100%',
  transition: 'all 0.3s ease',
  border: selected ? `2px solid ${CHOLA_COLORS.primary}` : `1px solid ${theme.palette.divider}`,
  boxShadow: selected ? `0 4px 12px ${CHOLA_COLORS.primary}25` : theme.shadows[1],
  '&:hover': {
    boxShadow: `0 6px 16px ${CHOLA_COLORS.primary}30`,
    transform: 'translateY(-4px)',
  },
}));

const UploadZone = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: theme.palette.background.default,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: CHOLA_COLORS.primary,
    backgroundColor: `${CHOLA_COLORS.primary}05`,
  },
}));

// API Base URL
const API_BASE_URL = 'https://renewals-insuremind-backend.neuralkart.com/api';

// File upload API endpoint mapping
const UPLOAD_ENDPOINTS: Record<string, string> = {
  'lead': '/uploads/lead-data',
  'hierarchy': '/uploads/hierarchy-data',
  'lead-status': '/uploads/lead-status',
  'renewals': '/uploads/renewal-targets',
  'products': '/uploads/products',
  // Propensity predictions upload
  'propensity': '/propensity/propensity-predictions'
};

// Status check API endpoint mapping
const STATUS_ENDPOINTS: Record<string, string> = {
  'lead': '/uploads/lead-data/status',
  'hierarchy': '/uploads/hierarchy-data/status',
  'lead-status': '/uploads/lead-status/status',
  'renewals': '/uploads/renewal-targets/status',
  'products': '/uploads/products/status',
  // Propensity predictions status
  'propensity': '/propensity/propensity-predictions/status'
};

// API endpoints for filters
const FILTERS_ENDPOINT = '/dashboard/filters';

// Status types for task processing
export enum UploadTaskStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  WARNING = 'warning'
}

interface UploadTask {
  task_id: string;
  status: UploadTaskStatus;
  message: string;
  progress?: number;
  file_name?: string;
  created_at?: string;
  completed_at?: string;
  error_details?: string;
  warnings?: string[];
}

interface UploadHistoryItem {
  task_id: string;
  status: UploadTaskStatus;
  message: string;
  file_name: string;
  created_at: string;
  completed_at?: string;
}

interface FileTypeOption {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  adminOnly: boolean;
}

// API function to upload a file
const uploadFile = async (file: File, fileType: string, truncate: boolean = false, month?: string, year?: string, channel?: string): Promise<{ task_id: string; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const endpoint = UPLOAD_ENDPOINTS[fileType];
  if (!endpoint) {
    throw new Error(`Unknown file type: ${fileType}`);
  }

  // Build URL with query parameters for supported endpoints
  let url = `${API_BASE_URL}${endpoint}`;
  
  // Add query parameters based on file type
  const params = new URLSearchParams();
  
  if (['lead', 'hierarchy', 'renewals'].includes(fileType)) {
    params.append('truncate', truncate.toString());
  }
  
  // Add month and year parameters for lead data when truncate is true
  if (fileType === 'lead' && truncate) {
    if (month) params.append('month', month);
    if (year) params.append('year', year);
  }
  
  // Add channel parameter for hierarchy data when truncate is true
  if (fileType === 'hierarchy' && truncate && channel) {
    params.append('channel', channel);
  }
  
  // Append params to URL if any exist
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    // Clear all caches after successful data upload
    // This ensures that the dashboard will fetch fresh data reflecting the upload
    clearCachesAfterUpload();
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.detail || 'Failed to upload file');
    }
    throw new Error('Network error occurred');
  }
};

// API function to check task status
const checkTaskStatus = async (taskId: string, fileType: string): Promise<UploadTask> => {
  const endpoint = STATUS_ENDPOINTS[fileType];
  if (!endpoint) {
    throw new Error(`Unknown file type: ${fileType}`);
  }

  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.get(`${API_BASE_URL}${endpoint}/${taskId}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    // Clear all caches if the task is completed
    // This ensures the dashboard will fetch fresh data
    if (response.data.status === UploadTaskStatus.COMPLETED) {
      clearCachesAfterUpload();
    }
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.detail || 'Failed to check task status');
    }
    throw new Error('Network error occurred');
  }
};

// API function to fetch upload history for a file type
const fetchUploadHistory = async (fileType: string): Promise<UploadHistoryItem[]> => {
  const endpoint = STATUS_ENDPOINTS[fileType];
  if (!endpoint) {
    throw new Error(`Unknown file type: ${fileType}`);
  }

  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.detail || 'Failed to fetch upload history');
    }
    throw new Error('Network error occurred');
  }
};

// API function to fetch filter options
const fetchFilterOptions = async (): Promise<{
  regions: string[];
  channel: string[];
  subchannel: string[];
  lob: string[];
  product_group: string[];
  sub_product: string[];
  months: { id: number; name: string; }[];
  years: { id: string; name: string; start_year: number; end_year: number; }[];
}> => {
  try {
    const token = localStorage.getItem('kc_token');
    const response = await axios.get(`${API_BASE_URL}${FILTERS_ENDPOINT}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to fetch filter options:', error);
    // Return default empty structure
    return {
      regions: [],
      channel: [],
      subchannel: [],
      lob: [],
      product_group: [],
      sub_product: [],
      months: [],
      years: []
    };
  }
};

export function ExcelUpload() {
  const [fileType, setFileType] = useState('hierarchy');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [currentTask, setCurrentTask] = useState<UploadTask | null>(null);
  const [uploadHistory, setUploadHistory] = useState<UploadHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [truncateData, setTruncateData] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<{
    regions: string[];
    channel: string[];
    subchannel: string[];
    lob: string[];
    product_group: string[];
    sub_product: string[];
    months: { id: number; name: string; }[];
    years: { id: string; name: string; start_year: number; end_year: number; }[];
  }>({
    regions: [],
    channel: [],
    subchannel: [],
    lob: [],
    product_group: [],
    sub_product: [],
    months: [],
    years: []
  });
  
  const pollingTimeoutRef = useRef<number | null>(null);
  
  // Get auth context to check user roles
  const { token } = useAuth();
  
  // Check if user has Admin role
  const hasAdminRole = React.useMemo(() => {
    if (!token) return false;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const parsedToken = JSON.parse(jsonPayload);
      return parsedToken.realm_access?.roles?.includes('Admin') || false;
    } catch (error) {
      console.error('Error checking admin role:', error);
      return false;
    }
  }, [token]);
  
  // Define all file type options
  const allFileTypeOptions: FileTypeOption[] = [
    {
      value: 'hierarchy',
      label: 'Hierarchy Data',
      description: 'Upload organizational hierarchy structure',
      icon: <InsertDriveFileIcon sx={{ color: CHOLA_COLORS.primary, fontSize: 40 }} />,
      adminOnly: true
    },
    {
      value: 'lead',
      label: 'Lead Data',
      description: 'Upload customer lead information',
      icon: <InsertDriveFileIcon sx={{ color: CHOLA_COLORS.secondary, fontSize: 40 }} />,
      adminOnly: true
    },
    {
      value: 'lead-status',
      label: 'Lead Status Data',
      description: 'Upload lead status details and information',
      icon: <InsertDriveFileIcon sx={{ color: CHOLA_COLORS.accent, fontSize: 40 }} />,
      adminOnly: false
    },
    {
      value: 'renewals',
      label: 'Renewal Targets',
      description: 'Upload renewal target information',
      icon: <InsertDriveFileIcon sx={{ color: '#008000', fontSize: 40 }} />,
      adminOnly: true
    },
    {
      value: 'products',
      label: 'Products Data',
      description: 'Upload product catalog and information',
      icon: <InsertDriveFileIcon sx={{ color: '#9c27b0', fontSize: 40 }} />,
      adminOnly: true
    },
    {
      value: 'propensity',
      label: 'Propensity Predictions',
      description: 'Upload predictions CSV and track async processing',
      icon: <InsertDriveFileIcon sx={{ color: '#2e7d32', fontSize: 40 }} />,
      adminOnly: true
    }
  ];
  
  // Filter file type options based on admin role
  const fileTypeOptions = allFileTypeOptions.filter(option => 
    !option.adminOnly || hasAdminRole
  );
  
  // Update fileType if current selection is no longer available
  useEffect(() => {
    if (!fileTypeOptions.some(option => option.value === fileType) && fileTypeOptions.length > 0) {
      setFileType(fileTypeOptions[0].value);
    }
  }, [fileTypeOptions, fileType]);
  
  // Load upload history when file type changes
  useEffect(() => {
    loadUploadHistory();
    
    // Clear any active polling when changing file types
    if (pollingTimeoutRef.current) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    
    setCurrentTask(null);
  }, [fileType]);

  // Load filter options when component mounts
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await fetchFilterOptions();
        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    
    loadFilterOptions();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        window.clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const loadUploadHistory = async () => {
    try {
      const history = await fetchUploadHistory(fileType);
      setUploadHistory(history);
    } catch (err) {
      console.error('Failed to load upload history:', err);
      // Don't show an error to the user for history loading failures
    }
  };

  const startPolling = (taskId: string) => {
    // Define a polling function
    const pollStatus = async () => {
      try {
        const taskStatus = await checkTaskStatus(taskId, fileType);
        setCurrentTask(taskStatus);
        
        // If the task is completed or failed, stop polling
        if (
          taskStatus.status === UploadTaskStatus.COMPLETED || 
          taskStatus.status === UploadTaskStatus.FAILED
        ) {
          if (taskStatus.status === UploadTaskStatus.COMPLETED) {
            setSuccess(true);
            setSuccessMessage(taskStatus.message || 'File processed successfully');
            setOpenSnackbar(true);
          } else if (taskStatus.status === UploadTaskStatus.FAILED) {
            setError(taskStatus.message || 'File processing failed');
          }
          
          setLoading(false);
          // Refresh the upload history
          loadUploadHistory();
          
          // Clear polling timeout
          if (pollingTimeoutRef.current) {
            window.clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        } else {
          // Continue polling if the task is not yet complete
          pollingTimeoutRef.current = window.setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        console.error('Error polling task status:', err);
        setError('Failed to check task status. Please refresh to see the latest status.');
        setLoading(false);
        
        // Clear polling timeout on error
        if (pollingTimeoutRef.current) {
          window.clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
      }
    };

    // Start polling immediately
    pollStatus();
  };

  const handleFileTypeChange = (newFileType: string) => {
    setFileType(newFileType);
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    setCurrentTask(null);
    setTruncateData(false);
    setSelectedMonth('');
    setSelectedYear('');
    setSelectedChannel('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // For propensity, only allow CSV
      const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel' || file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      const allowed = fileType === 'propensity' ? isCsv : (isExcel || isCsv);
      if (allowed) {
        setSelectedFile(file);
        setError(null);
        setSuccess(false);
        setCurrentTask(null);
      } else {
        setError(fileType === 'propensity'
          ? 'Please upload only CSV files (.csv) for Propensity Predictions'
          : 'Please upload only Excel files (.xlsx or .xls) or CSV (.csv)');
        setSelectedFile(null);
        setSuccess(false);
        setCurrentTask(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    // Validate month and year when truncate is enabled for lead data
    if (fileType === 'lead' && truncateData) {
      if (selectedMonth && !selectedMonth.match(/^([1-9]|1[0-2])$/)) {
        setError('Please enter a valid month (1-12)');
        return;
      }
      
      if (selectedYear && !selectedYear.match(/^\d{4}$/)) {
        setError('Please enter a valid 4-digit year');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setCurrentTask(null);
    
    try {
      // Pass parameters based on file type
      const result = await uploadFile(
        selectedFile, 
        fileType, 
        truncateData,
        fileType === 'lead' && truncateData ? selectedMonth : undefined,
        fileType === 'lead' && truncateData ? selectedYear : undefined,
        fileType === 'hierarchy' && truncateData ? selectedChannel : undefined
      );
      
      // Set initial task information
      setCurrentTask({
        task_id: result.task_id,
        status: UploadTaskStatus.PENDING,
        message: result.message || 'Upload initialized',
        progress: 0
      });
      
      // Start polling for status updates
      startPolling(result.task_id);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const getSelectedFileType = () => {
    const option = fileTypeOptions.find(option => option.value === fileType);
    // Return first option or a fallback if none found
    return option || (fileTypeOptions.length > 0 ? fileTypeOptions[0] : {
      value: '',
      label: 'No options available',
      description: '',
      icon: null,
      adminOnly: false
    });
  };

  const getStatusIcon = (status: UploadTaskStatus) => {
    switch (status) {
      case UploadTaskStatus.COMPLETED:
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case UploadTaskStatus.FAILED:
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case UploadTaskStatus.PROCESSING:
      case UploadTaskStatus.PENDING:
        return <PendingIcon sx={{ color: CHOLA_COLORS.primary }} />;
      case UploadTaskStatus.WARNING:
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      default:
        return <HelpOutlineIcon />;
    }
  };

  // Get list of years (current year and 5 years back)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - i);
  };

  // Get list of months
  const getMonthOptions = () => {
    return [
      { value: '1', label: 'January' },
      { value: '2', label: 'February' },
      { value: '3', label: 'March' },
      { value: '4', label: 'April' },
      { value: '5', label: 'May' },
      { value: '6', label: 'June' },
      { value: '7', label: 'July' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' },
    ];
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: CHOLA_COLORS.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1
          }}
        >
          <FileUploadIcon sx={{ color: CHOLA_COLORS.secondary }} />
          Data Upload Center
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
          Upload and process Excel files to update system data
        </Typography>
        
        <Grid container spacing={3}>
          {/* Left side - File Type Selection */}
          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 2,
                boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
                border: `1px solid ${CHOLA_COLORS.grey}`
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: CHOLA_COLORS.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <DescriptionIcon sx={{ color: CHOLA_COLORS.secondary }} />
                Select File Type
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                {fileTypeOptions.map((option) => (
                  <Grid item xs={12} key={option.value}>
                    <FileTypeCard 
                      selected={fileType === option.value}
                      onClick={() => handleFileTypeChange(option.value)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {option.icon}
                        <Box>
                          <Typography variant="subtitle1" fontWeight={fileType === option.value ? 600 : 400}>
                            {option.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </FileTypeCard>
                  </Grid>
                ))}
              </Grid>

              {/* Upload History Button */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button 
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setShowHistory(!showHistory)}
                  sx={{ 
                    mt: 2,
                    color: CHOLA_COLORS.primary,
                    borderColor: CHOLA_COLORS.primary,
                  }}
                >
                  {showHistory ? 'Hide Upload History' : 'Show Upload History'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Right side - Upload Area or History */}
          <Grid item xs={12} md={8}>
            {showHistory ? (
              <Paper 
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
                  border: `1px solid ${CHOLA_COLORS.grey}`
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: CHOLA_COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <HistoryIcon sx={{ color: CHOLA_COLORS.secondary }} />
                    Upload History
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={loadUploadHistory}
                    variant="text"
                  >
                    Refresh
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                {uploadHistory.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No upload history found for {getSelectedFileType().label}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {uploadHistory.map((item) => (
                      <ListItem 
                        key={item.task_id}
                        divider
                        secondaryAction={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.created_at).toLocaleString()}
                          </Typography>
                        }
                      >
                        <ListItemIcon>
                          {getStatusIcon(item.status)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.file_name || 'Unknown file'}
                          secondary={item.message}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            ) : (
              <Paper 
                sx={{ 
                  p: 3,
                  borderRadius: 2,
                  boxShadow: `0 0 20px ${CHOLA_COLORS.primary}15`,
                  border: `1px solid ${CHOLA_COLORS.grey}`
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: CHOLA_COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <CloudUploadIcon sx={{ color: CHOLA_COLORS.secondary }} />
                    Upload {getSelectedFileType().label}
                  </Typography>
                  <Tooltip title={fileType === 'propensity' ? 'Supported format: .csv' : 'Supported formats: .xlsx, .xls, .csv'}>
                    <IconButton size="small">
                      <HelpOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                {success ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 80, 
                        color: 'success.main',
                      }} 
                    />
                    <Typography variant="h6" color="success.main">
                      {successMessage}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setSelectedFile(null);
                        setSuccess(false);
                        setCurrentTask(null);
                      }}
                      sx={{ mt: 2 }}
                    >
                      Upload Another File
                    </Button>
                  </Box>
                ) : (
                  <>
                    <UploadZone>
                      <Button
                        component="label"
                        variant={selectedFile ? "outlined" : "contained"}
                        startIcon={<CloudUploadIcon />}
                        size="large"
                        sx={{ 
                          minWidth: '200px',
                          minHeight: '48px',
                          mb: 2
                        }}
                        disabled={loading}
                      >
                        {selectedFile ? "Change File" : `Select ${getSelectedFileType().label} File`}
                        <VisuallyHiddenInput
                          type="file"
                          accept={fileType === 'propensity' ? '.csv' : '.xlsx,.xls,.csv'}
                          onChange={handleFileChange}
                        />
                      </Button>
                      
                      {selectedFile ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: 1,
                          p: 2,
                          bgcolor: `${CHOLA_COLORS.primary}08`,
                          borderRadius: 1
                        }}>
                          <InsertDriveFileIcon sx={{ color: CHOLA_COLORS.primary }} />
                          <Typography variant="body1" fontWeight={500}>
                            {selectedFile.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Drag and drop your Excel file here, or click to browse
                        </Typography>
                      )}
                    </UploadZone>

                    {/* Task status display */}
                    {currentTask && (
                      <Box sx={{ mt: 3, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2">
                            Processing Status: {currentTask.status.charAt(0).toUpperCase() + currentTask.status.slice(1)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getStatusIcon(currentTask.status)}
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {currentTask.message}
                        </Typography>
                        {(currentTask.status === UploadTaskStatus.PENDING || currentTask.status === UploadTaskStatus.PROCESSING) && (
                          <LinearProgress 
                            variant={currentTask.progress !== undefined ? "determinate" : "indeterminate"} 
                            value={currentTask.progress}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        )}
                        {currentTask.warnings && currentTask.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">Warnings:</Typography>
                            <ul>
                              {currentTask.warnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                              ))}
                            </ul>
                          </Alert>
                        )}
                      </Box>
                    )}

                    {error && (
                      <Alert severity="error" sx={{ mt: 3 }}>
                        {error}
                      </Alert>
                    )}

                    {/* Truncate option - only show for supported file types */}
                    {['lead', 'hierarchy', 'renewals'].includes(fileType) && (
                      <Box mt={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Checkbox
                            checked={truncateData}
                            onChange={(event) => setTruncateData(event.target.checked)}
                            color="primary"
                            id="truncate-checkbox"
                          />
                          <Tooltip title="When enabled, all existing data will be removed before importing the new data. When disabled, new data will be added to existing data.">
                            <Typography variant="body2" color="text.secondary" component="label" htmlFor="truncate-checkbox" sx={{ cursor: 'pointer' }}>
                              Replace existing data (truncate)
                            </Typography>
                          </Tooltip>
                        </Stack>
                        
                        {/* Month and Year selection for Lead data */}
                        {fileType === 'lead' && truncateData && (
                          <Box mt={2} sx={{ p: 2, bgcolor: `${CHOLA_COLORS.primary}08`, borderRadius: 1 }}>
                            <Typography variant="subtitle2" color={CHOLA_COLORS.primary} mb={2}>
                              Optional: Select specific month/year to delete data for
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Month (optional)"
                                  value={selectedMonth}
                                  onChange={(e) => setSelectedMonth(e.target.value)}
                                  size="small"
                                  variant="outlined"
                                  helperText="Select to delete data for specific months (1-12)"
                                >
                                  <MenuItem value="">
                                    <em>All months</em>
                                  </MenuItem>
                                  {getMonthOptions().map((month) => (
                                    <MenuItem key={month.value} value={month.value}>
                                      {month.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Year (optional)"
                                  value={selectedYear}
                                  onChange={(e) => setSelectedYear(e.target.value)}
                                  size="small"
                                  variant="outlined"
                                  helperText="Select to delete data for specific years"
                                >
                                  <MenuItem value="">
                                    <em>All years</em>
                                  </MenuItem>
                                  {getYearOptions().map((year) => (
                                    <MenuItem key={year} value={year.toString()}>
                                      {year}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        
                        {/* Channel selection for Hierarchy data */}
                        {fileType === 'hierarchy' && truncateData && (
                          <Box mt={2} sx={{ p: 2, bgcolor: `${CHOLA_COLORS.primary}08`, borderRadius: 1 }}>
                            <Typography variant="subtitle2" color={CHOLA_COLORS.primary} mb={2}>
                              Optional: Select specific channel to delete data for
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  select
                                  fullWidth
                                  label="Channel (optional)"
                                  value={selectedChannel}
                                  onChange={(e) => setSelectedChannel(e.target.value)}
                                  size="small"
                                  variant="outlined"
                                  helperText="Select to truncate data for a specific channel only"
                                >
                                  <MenuItem value="">
                                    <em>All channels</em>
                                  </MenuItem>
                                  {filterOptions.channel.map((channel) => (
                                    <MenuItem key={channel} value={channel}>
                                      {channel}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpload}
                      disabled={!selectedFile || loading}
                      fullWidth
                      size="large"
                      sx={{ 
                        mt: 3,
                        bgcolor: CHOLA_COLORS.primary,
                        '&:hover': {
                          bgcolor: CHOLA_COLORS.hover
                        }
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                          Uploading...
                        </Box>
                      ) : (
                        'Upload File'
                      )}
                    </Button>
                  </>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
    </Container>
  );
} 
